import React, { useState, useEffect } from 'react';
import { Button, TextField, List, ListItem, IconButton, Card, CardContent, Typography, Autocomplete } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';
import './App.css';

function App() {
  const [allIngredients, setAllIngredients] = useState([]);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [recipeDetails, setRecipeDetails] = useState({});
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    const ingredients = [
      "Pomme", "Banane", "Orange", "Mangue", "Poulet", "Tomate", "Poivre", "Lait", "Fromage"
    ];
    setAllIngredients(ingredients);

    const savedIngredients = JSON.parse(localStorage.getItem('selectedIngredients'));
    if (savedIngredients) {
      setSelectedIngredients(savedIngredients);
    }

    const savedRecipesData = JSON.parse(localStorage.getItem('savedRecipes'));
    if (savedRecipesData) {
      setSavedRecipes(savedRecipesData);
    }
  }, []);

  const addIngredient = (ingredient) => {
    if (!selectedIngredients.includes(ingredient)) {
      const updatedIngredients = [...selectedIngredients, ingredient];
      setSelectedIngredients(updatedIngredients);
      localStorage.setItem('selectedIngredients', JSON.stringify(updatedIngredients));
    }
  };

  const removeIngredient = (ingredient) => {
    const updatedIngredients = selectedIngredients.filter(item => item !== ingredient);
    setSelectedIngredients(updatedIngredients);
    localStorage.setItem('selectedIngredients', JSON.stringify(updatedIngredients));
  };

  const clearIngredients = () => {
    setSelectedIngredients([]);
    localStorage.removeItem('selectedIngredients');
  };

  const getRecipes = async () => {
    try {
     const prompt = `J'ai les ingrédients suivants : ${selectedIngredients.join(', ')}. Peux-tu me donner uniquement les noms de 10 recettes que je peux réaliser avec ces ingrédients et rien d'autre ?`;

      const response = await axios.post('https://api.openai.com/v1/chat/completions', {
        model: 'gpt-3.5-turbo',
        messages: [{ role: 'user', content: prompt }],
        max_tokens: 150,
        temperature: 0.7,
      }, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer sk-mcTBEFjSYHL0eMi53jVhVfgIFZ7XyFEh-AFVsQlROtT3BlbkFJNQY1hARmjeuQV_TNkc9LdSWKGrK67TQ63LMxZ-_38A`,
          },
      });

      if (response.data && response.data.choices && response.data.choices.length > 0) {
        const generatedRecipes = response.data.choices[0].message.content.trim().split('\n');
        setRecipes(generatedRecipes);
      } else {
        setRecipes([]);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des recettes:', error);
    }
  };

  const saveRecipe = (recipe, details) => {
    if (!savedRecipes.some((saved) => saved.recipe === recipe)) {
      const updatedSavedRecipes = [...savedRecipes, { recipe, details }];
      setSavedRecipes(updatedSavedRecipes);
      localStorage.setItem('savedRecipes', JSON.stringify(updatedSavedRecipes));
    }
  };

  const showRecipeDetails = async (recipe) => {
    try {
        const prompt = `Je dispose des ingrédients suivants : ${selectedIngredients.join(', ')}. Peux-tu me donner les instructions détaillées pour la recette ${recipe} en utilisant uniquement ces ingrédients et des ingrédients de base (comme sel, huile, poivre) ? Formate la réponse en HTML dans ce format :

        <h3>Liste des ingrédients</h3>
        <ol>
          <p>Ingrédient 1 avec quantité</p>
          <p>Ingrédient 2 avec quantité</p>
          <!-- Liste des autres ingrédients -->
        </ol>
        
        <h3>Temps de préparation et cuisson</h3>
        <p>Temps de préparation : X minutes</p>
        <p>Temps de cuisson : X minutes</p>
        
        <h3>Instructions</h3>
        <ol>
          <li>Étape 1 détaillée</li>
          <li>Étape 2 détaillée</li>
          <!-- Autres étapes -->
        </ol>`;
        
        
      const response = await axios.post('https://api.openai.com/v1/chat/completions', {
        model: 'gpt-3.5-turbo',
        messages: [{ role: 'user', content: prompt }],
        max_tokens: 500,
        temperature: 0.7,
      }, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer sk-mcTBEFjSYHL0eMi53jVhVfgIFZ7XyFEh-AFVsQlROtT3BlbkFJNQY1hARmjeuQV_TNkc9LdSWKGrK67TQ63LMxZ-_38A`,
          },
      });

      if (response.data && response.data.choices && response.data.choices.length > 0) {
        const details = response.data.choices[0].message.content.trim();
        setRecipeDetails((prev) => ({ ...prev, [recipe]: details }));
      } else {
        setRecipeDetails((prev) => ({ ...prev, [recipe]: 'Aucun détail trouvé.' }));
      }
    } catch (error) {
      setRecipeDetails((prev) => ({ ...prev, [recipe]: 'Erreur lors de la récupération des détails.' }));
    }
  };

  const removeSavedRecipe = (recipeName) => {
    const updatedSavedRecipes = savedRecipes.filter((saved) => saved.recipe !== recipeName);
    setSavedRecipes(updatedSavedRecipes);
    localStorage.setItem('savedRecipes', JSON.stringify(updatedSavedRecipes));
  };

  const clearLocalStorage = () => {
    localStorage.clear();
    setSelectedIngredients([]);
    setSavedRecipes([]);
    setRecipes([]);
  };

  return (
    <div className="App">
      <Typography variant="h4" component="h1" gutterBottom>
        Sélectionne tes Ingrédients
      </Typography>

      <Button variant="contained" color="secondary" onClick={clearLocalStorage}>
        Vider tout (Ingrédients et Recettes)
      </Button>

      <Autocomplete
        options={allIngredients}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
        onChange={(event, value) => {
          if (value) {
            addIngredient(value);
            setInputValue('');
          }
        }}
        renderInput={(params) => (
          <TextField {...params} label="Rechercher un ingrédient" variant="outlined" fullWidth margin="normal" />
        )}
      />

      <Typography variant="h6" component="h2">
        Ingrédients Sélectionnés
      </Typography>

      <List>
        {selectedIngredients.map((ingredient, index) => (
          <ListItem
            key={index}
            secondaryAction={
              <IconButton edge="end" aria-label="delete" onClick={() => removeIngredient(ingredient)}>
                <DeleteIcon />
              </IconButton>
            }
          >
            <Typography variant="body1">{ingredient}</Typography>
          </ListItem>
        ))}
      </List>

      {selectedIngredients.length > 0 && (
        <Button variant="outlined" color="error" onClick={clearIngredients}>
          Vider la Liste des Ingrédients
        </Button>
      )}

      <Button variant="contained" color="primary" onClick={getRecipes} style={{ marginTop: '20px' }}>
        Générer des Recettes
      </Button>

      <Typography variant="h6" component="h2" style={{ marginTop: '20px' }}>
        Recettes Suggérées
      </Typography>

      <List>
        {recipes.map((recipe, index) => (
          <Card key={index} variant="outlined" style={{ marginBottom: '20px' }}>
            <CardContent>
  <Typography variant="h5" component="div" onClick={() => showRecipeDetails(recipe)}>
    {recipe}
  </Typography>
  {recipeDetails[recipe] && (
    <div>
      <Typography variant="body2" color="text.secondary" dangerouslySetInnerHTML={{ __html: recipeDetails[recipe] }} />
      <Button
        variant="contained"
        color="success"
        startIcon={<SaveIcon />}
        onClick={() => saveRecipe(recipe, recipeDetails[recipe])}
      >
        Sauvegarder
      </Button>
    </div>
  )}
</CardContent>

          </Card>
        ))}
      </List>
      <Typography variant="h6" component="h2" style={{ marginTop: '20px' }}>
  Recettes Sauvegardées
</Typography>

<List className="saved-recipes">
  {savedRecipes.map((recipeObj, index) => (
    <Card key={index} variant="outlined" style={{ marginBottom: '20px' }}>
      <CardContent>
        <Typography variant="h6">{recipeObj.recipe}</Typography>
        <Typography variant="body2" dangerouslySetInnerHTML={{ __html: recipeObj.details }} />
        <IconButton
          edge="end"
          aria-label="delete"
          onClick={() => removeSavedRecipe(recipeObj.recipe)}
        >
          <DeleteIcon />
        </IconButton>
      </CardContent>
    </Card>
  ))}
</List>

    </div>
  );
}

export default App;